import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { toast } from "sonner";
import { Check, X, Plus, Trash2, FileText, ShieldCheck, Ban, Percent } from "lucide-react";

const SOURCE_BADGE = {
  tier: { v: "success", t: "Included" }, addon: { v: "warning", t: "Add-on" },
  granted: { v: "success", t: "Granted" }, revoked: { v: "destructive", t: "Revoked" },
  none: { v: "outline", t: "Not included" },
};
const CHARGE_TYPES = [
  { v: "professional_service", l: "Professional service" },
  { v: "support", l: "Support" },
  { v: "adjustment", l: "Adjustment / credit" },
  { v: "one_off", l: "One-off charge" },
];
const DISCOUNT_TARGETS = [
  { v: "subscription", l: "Subscription only" },
  { v: "addon", l: "A specific add-on" },
  { v: "managed_service", l: "Managed service" },
  { v: "subtotal", l: "Whole invoice subtotal" },
];

export default function PlatformTenantDialog({ tenant, onClose, onChanged }) {
  const [ent, setEnt] = useState(null);
  const [charges, setCharges] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [form, setForm] = useState({ type: "professional_service", description: "", amount_sgd: "", recurring: false });
  const [dform, setDform] = useState({ label: "", discount_type: "pct", value: "", target: "subscription", target_key: "", recurring: true, starts_on: "", ends_on: "" });
  const tid = tenant.id;

  const load = useCallback(() => {
    api.get(`/billing/platform/tenants/${tid}/entitlements`).then((r) => setEnt(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get(`/billing/platform/tenants/${tid}/charges`).then((r) => setCharges(r.data.charges || [])).catch(() => {});
    api.get(`/billing/platform/tenants/${tid}/discounts`).then((r) => setDiscounts(r.data.discounts || [])).catch(() => {});
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const setOverride = async (key, entitled) => {
    try { await api.post(`/billing/platform/tenants/${tid}/override`, { feature_key: key, entitled }); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const addCharge = async () => {
    if (!form.description || !form.amount_sgd) { toast.error("Description and amount required"); return; }
    try {
      await api.post(`/billing/platform/tenants/${tid}/charges`, {
        type: form.type, description: form.description, amount_sgd: Number(form.amount_sgd), recurring: form.recurring,
      });
      toast.success("Charge added");
      setForm({ type: "professional_service", description: "", amount_sgd: "", recurring: false });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delCharge = async (cid) => {
    try { await api.delete(`/billing/platform/charges/${cid}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const addonFeatures = (ent?.features || []).filter((f) => f.addon);
  const addDiscount = async () => {
    if (!dform.label.trim() || !dform.value) { toast.error("Label and value required"); return; }
    if (dform.target === "addon" && !dform.target_key) { toast.error("Pick which add-on to discount"); return; }
    try {
      await api.post(`/billing/platform/tenants/${tid}/discounts`, {
        label: dform.label.trim(), discount_type: dform.discount_type, value: Number(dform.value),
        target: dform.target, target_key: dform.target === "addon" ? dform.target_key : null, recurring: dform.recurring,
        starts_on: dform.starts_on || null, ends_on: dform.ends_on || null,
      });
      toast.success("Discount added");
      setDform({ label: "", discount_type: "pct", value: "", target: "subscription", target_key: "", recurring: true, starts_on: "", ends_on: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delDiscount = async (did) => {
    try { await api.delete(`/billing/platform/discounts/${did}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const genInvoice = async () => {
    try {
      const { data } = await api.post(`/billing/platform/tenants/${tid}/invoices/generate`, {});
      toast.success(`Invoice ${data.invoice_number} generated — S$${formatNum(data.total, { decimals: 2 })}`);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="platform-tenant-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-accent" /> {tenant.name}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 -mt-1">
          {ent?.tier && <Badge variant="outline">Plan: {ent.tier}</Badge>}
          <Button size="sm" variant="primary" onClick={genInvoice} data-testid="generate-invoice-btn"><FileText className="h-3.5 w-3.5" /> Generate this month's invoice</Button>
        </div>

        <Tabs defaultValue="entitlements" className="mt-2">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="entitlements" data-testid="dlg-tab-entitlements">Entitlements</TabsTrigger>
            <TabsTrigger value="charges" data-testid="dlg-tab-charges">Charges</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="dlg-tab-discounts">Discounts</TabsTrigger>
          </TabsList>

          <TabsContent value="entitlements" className="mt-3 space-y-1.5">
            {!ent ? <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div> :
              ent.features.filter((f) => f.addon || f.source !== "tier").map((f) => {
                const sb = SOURCE_BADGE[f.source] || SOURCE_BADGE.none;
                return (
                  <div key={f.key} className="flex items-center gap-3 rounded-sm border border-border p-2.5" data-testid={`ent-row-${f.key}`}>
                    <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{f.name}</div><div className="text-[11px] text-muted-foreground">{f.group}{f.addon_price_sgd ? ` · add-on S$${f.addon_price_sgd}/mo` : ""}</div></div>
                    <Badge variant={f.entitled ? "success" : "outline"} className="text-[9px]">{f.entitled ? <Check className="h-3 w-3 mr-0.5" /> : <X className="h-3 w-3 mr-0.5" />}{sb.t}</Badge>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" title="Grant" onClick={() => setOverride(f.key, true)} data-testid={`grant-${f.key}`}><Check className="h-3.5 w-3.5 text-[#00E65B]" /></Button>
                      <Button size="sm" variant="ghost" title="Revoke" onClick={() => setOverride(f.key, false)} data-testid={`revoke-${f.key}`}><Ban className="h-3.5 w-3.5 text-red-500" /></Button>
                      <Button size="sm" variant="ghost" title="Clear override" onClick={() => setOverride(f.key, null)}><X className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                    </div>
                  </div>
                );
              })}
          </TabsContent>

          <TabsContent value="charges" className="mt-3 space-y-3">
            <div className="card-flat p-3 grid grid-cols-1 sm:grid-cols-[160px_1fr_110px_auto] gap-2 items-end" data-testid="add-charge-form">
              <div><div className="label-eyebrow text-[9px] mb-1">Type</div>
                <Select value={form.type} onValueChange={(v) => setForm((s) => ({ ...s, type: v }))}>
                  <SelectTrigger data-testid="charge-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHARGE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><div className="label-eyebrow text-[9px] mb-1">Description</div><Input value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} placeholder="e.g. Assurance advisory (2 days)" data-testid="charge-desc" /></div>
              <div><div className="label-eyebrow text-[9px] mb-1">Amount S$</div><Input type="number" value={form.amount_sgd} onChange={(e) => setForm((s) => ({ ...s, amount_sgd: e.target.value }))} data-testid="charge-amount" /></div>
              <Button variant="primary" onClick={addCharge} data-testid="add-charge-btn"><Plus className="h-3.5 w-3.5" /> Add</Button>
              <label className="text-[11px] text-muted-foreground flex items-center gap-1.5 sm:col-span-4 cursor-pointer"><input type="checkbox" checked={form.recurring} onChange={(e) => setForm((s) => ({ ...s, recurring: e.target.checked }))} data-testid="charge-recurring" /> Recurring (bill every month)</label>
            </div>
            <div className="space-y-1.5">
              {charges.map((c) => (
                <div key={c.id} className="flex items-center gap-3 rounded-sm border border-border p-2.5" data-testid={`charge-${c.id}`}>
                  <Badge variant="outline" className="text-[9px]">{c.type}</Badge>
                  <div className="min-w-0 flex-1 text-sm truncate">{c.description}</div>
                  {c.recurring && <Badge variant="warning" className="text-[9px]">recurring</Badge>}
                  {c.invoiced && <Badge variant="success" className="text-[9px]">invoiced</Badge>}
                  <span className="font-mono text-sm">S${formatNum(c.amount_sgd, { decimals: 2 })}</span>
                  <Button size="sm" variant="ghost" onClick={() => delCharge(c.id)} data-testid={`del-charge-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                </div>
              ))}
              {!charges.length && <div className="text-sm text-muted-foreground py-4 text-center">No manual charges.</div>}
            </div>
          </TabsContent>

          <TabsContent value="discounts" className="mt-3 space-y-3">
            <p className="text-[11px] text-muted-foreground -mt-1">Discounts appear as negative line items on the tenant's generated invoice. A percentage can't take a line below S$0.</p>
            <div className="card-flat p-3 grid grid-cols-1 sm:grid-cols-[1fr_120px_110px] gap-2 items-end" data-testid="add-discount-form">
              <div><div className="label-eyebrow text-[9px] mb-1">Label</div><Input value={dform.label} onChange={(e) => setDform((s) => ({ ...s, label: e.target.value }))} placeholder="e.g. Loyalty discount" data-testid="discount-label" /></div>
              <div><div className="label-eyebrow text-[9px] mb-1">Type</div>
                <Select value={dform.discount_type} onValueChange={(v) => setDform((s) => ({ ...s, discount_type: v }))}>
                  <SelectTrigger data-testid="discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pct">Percentage %</SelectItem>
                    <SelectItem value="fixed">Fixed S$</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><div className="label-eyebrow text-[9px] mb-1">{dform.discount_type === "pct" ? "Percent" : "Amount S$"}</div><Input type="number" value={dform.value} onChange={(e) => setDform((s) => ({ ...s, value: e.target.value }))} data-testid="discount-value" /></div>
              <div className="sm:col-span-2"><div className="label-eyebrow text-[9px] mb-1">Applies to</div>
                <Select value={dform.target} onValueChange={(v) => setDform((s) => ({ ...s, target: v, target_key: "" }))}>
                  <SelectTrigger data-testid="discount-target"><SelectValue /></SelectTrigger>
                  <SelectContent>{DISCOUNT_TARGETS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button variant="primary" onClick={addDiscount} data-testid="add-discount-btn"><Plus className="h-3.5 w-3.5" /> Add</Button>
              {dform.target === "addon" && (
                <div className="sm:col-span-3"><div className="label-eyebrow text-[9px] mb-1">Which add-on</div>
                  <Select value={dform.target_key} onValueChange={(v) => setDform((s) => ({ ...s, target_key: v }))}>
                    <SelectTrigger data-testid="discount-addon"><SelectValue placeholder="Select an add-on feature" /></SelectTrigger>
                    <SelectContent>
                      {addonFeatures.length
                        ? addonFeatures.map((f) => <SelectItem key={f.key} value={f.key}>{f.name}</SelectItem>)
                        : <SelectItem value="none" disabled>No add-on features defined</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="sm:col-span-3 grid grid-cols-2 gap-2">
                <div><div className="label-eyebrow text-[9px] mb-1">Starts (optional)</div><Input type="date" value={dform.starts_on} onChange={(e) => setDform((s) => ({ ...s, starts_on: e.target.value }))} data-testid="discount-starts" /></div>
                <div><div className="label-eyebrow text-[9px] mb-1">Ends (optional)</div><Input type="date" value={dform.ends_on} onChange={(e) => setDform((s) => ({ ...s, ends_on: e.target.value }))} data-testid="discount-ends" /></div>
              </div>
              <label className="text-[11px] text-muted-foreground flex items-center gap-1.5 sm:col-span-3 cursor-pointer"><input type="checkbox" checked={dform.recurring} onChange={(e) => setDform((s) => ({ ...s, recurring: e.target.checked }))} data-testid="discount-recurring" /> Recurring (apply to every invoice)</label>
            </div>
            <div className="space-y-1.5">
              {discounts.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-sm border border-border p-2.5" data-testid={`discount-${d.id}`}>
                  <Percent className="h-3.5 w-3.5 text-[#00E65B] shrink-0" />
                  <div className="min-w-0 flex-1 text-sm truncate">{d.label}
                    <span className="text-[11px] text-muted-foreground ml-1">· {DISCOUNT_TARGETS.find((t) => t.v === d.target)?.l || d.target}</span>
                    {(d.starts_on || d.ends_on) && (
                      <span className="text-[10px] text-muted-foreground block">
                        {d.starts_on ? `from ${d.starts_on}` : "now"} {d.ends_on ? `→ ${d.ends_on}` : "→ no end"}
                      </span>
                    )}
                  </div>
                  {d.ends_on && d.ends_on < new Date().toISOString().slice(0, 10) && <Badge variant="outline" className="text-[9px]" data-testid={`discount-expired-${d.id}`}>expired</Badge>}
                  {d.starts_on && d.starts_on > new Date().toISOString().slice(0, 10) && <Badge variant="outline" className="text-[9px]">scheduled</Badge>}
                  {d.recurring && <Badge variant="warning" className="text-[9px]">recurring</Badge>}
                  {d.invoiced && <Badge variant="success" className="text-[9px]">invoiced</Badge>}
                  <span className="font-mono text-sm text-[#00E65B]">−{d.discount_type === "pct" ? `${d.value}%` : `S$${formatNum(d.value, { decimals: 2 })}`}</span>
                  <Button size="sm" variant="ghost" onClick={() => delDiscount(d.id)} data-testid={`del-discount-${d.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                </div>
              ))}
              {!discounts.length && <div className="text-sm text-muted-foreground py-4 text-center">No discounts.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
